import React, { useState } from 'react';
import { DashboardPeriod, type ExternalDashboardFilters, type StoreOption, type UserOption } from '../types/dashboard.types';

interface DashboardFiltersProps {
  filters: ExternalDashboardFilters;
  onFiltersChange: (filters: ExternalDashboardFilters) => void;
  stores?: StoreOption[];
  availableUsers?: UserOption[];
  isLoading?: boolean;
}

const PERIOD_LABELS: Record<DashboardPeriod, string> = {
  [DashboardPeriod.DAY]: "Aujourd'hui",
  [DashboardPeriod.WEEK]: 'Cette semaine',
  [DashboardPeriod.MONTH]: 'Ce mois',
  [DashboardPeriod.YEAR]: 'Cette année',
  [DashboardPeriod.LAST_12_MONTHS]: '12 derniers mois',
  [DashboardPeriod.ALL]: 'Depuis le début',
  [DashboardPeriod.CUSTOM]: 'Personnalisé',
};

const labelClasses = 'tw-text-xs tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-shrink-0';
const selectClasses = 'tw-px-3 tw-py-1.5 tw-border tw-border-gray-300 tw-rounded-md focus:tw-ring-affinicar-primary focus:tw-border-affinicar-primary tw-text-sm tw-bg-white tw-min-w-0 tw-max-w-full';

const MIN_SELECTABLE_DATE = '2000-01-01';

/**
 * Une date de période est "complète" si elle est au format YYYY-MM-DD ET que son
 * année est plausible.
 *
 * Un <input type="date"> émet un onChange à chaque chiffre de l'année : taper
 * 2026 produit 0002-08-25, puis 0020-08-25, 0202-08-25 et enfin 2026-08-25.
 * Sans ce filtre, chaque frappe remonte au parent et déclenche une volée de
 * requêtes sur des plages absurdes (an 2 → aujourd'hui).
 */
const isCompleteDate = (value: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const year = Number(value.slice(0, 4));
  return year >= 2000 && year <= new Date().getFullYear() + 1;
};

export const DashboardFilters: React.FC<DashboardFiltersProps> = ({
  filters,
  onFiltersChange,
  stores = [],
  availableUsers = [],
  isLoading = false,
}) => {
  const committed = { start: filters.startDate ?? '', end: filters.endDate ?? '' };

  // Brouillon local : ce que l'utilisateur voit pendant qu'il tape, y compris les
  // états intermédiaires qu'on refuse de remonter au parent.
  const [draft, setDraft] = useState(committed);
  const [syncedFrom, setSyncedFrom] = useState(committed);

  // Resynchronisation quand les dates changent côté parent (changement de période,
  // reset…). Ajustement d'état pendant le rendu — pas de useEffect (cf CONTEXT.md).
  if (syncedFrom.start !== committed.start || syncedFrom.end !== committed.end) {
    setSyncedFrom(committed);
    setDraft(committed);
  }

  const today = new Date().toISOString().split('T')[0];

  const handlePeriodChange = (period: string) => {
    onFiltersChange({ ...filters, period, startDate: undefined, endDate: undefined });
  };

  const commitDates = (start: string, end: string) => {
    if (start === committed.start && end === committed.end) return;
    onFiltersChange({
      ...filters,
      period: DashboardPeriod.CUSTOM,
      startDate: start || undefined,
      endDate: end || undefined,
    });
  };

  const handleDateChange = (bound: 'start' | 'end', value: string) => {
    const next = { ...draft, [bound]: value };
    setDraft(next);
    // Saisie en cours : on garde la valeur en local sans requêter. Un champ vidé
    // est en revanche remonté tout de suite (la plage devient incomplète, les
    // requêtes se mettent en pause d'elles-mêmes).
    if (value !== '' && !isCompleteDate(value)) return;
    commitDates(next.start, next.end);
  };

  // Sortie de champ / Entrée : on applique le brouillon, ou on le restaure s'il
  // est resté sur une valeur inexploitable (année incomplète abandonnée).
  const flushDates = () => {
    const isUsable = (value: string) => value === '' || isCompleteDate(value);
    if (isUsable(draft.start) && isUsable(draft.end)) commitDates(draft.start, draft.end);
    else setDraft(committed);
  };

  const handleDateKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') flushDates();
  };

  // Changer de point de vente réinitialise l'utilisateur : celui qui était
  // sélectionné appartient rarement à la nouvelle agence, et le croisement
  // agence B / vendeur de l'agence A ne renvoie que du vide.
  const handleStoreChange = (storeId: string) => {
    onFiltersChange({
      ...filters,
      storeId: storeId || undefined,
      filterUserId: undefined,
    });
  };

  const handleUserChange = (filterUserId: string) => {
    onFiltersChange({ ...filters, filterUserId: filterUserId || undefined });
  };

  return (
    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 sm:tw-gap-x-6 tw-gap-y-2 tw-mb-4 tw-py-2 tw-max-w-full">
      {/* Période */}
      <div className="tw-flex tw-items-center tw-gap-2">
        <span className={labelClasses}>Période</span>
        <select
          value={filters.period || DashboardPeriod.MONTH}
          onChange={(e) => handlePeriodChange(e.target.value)}
          disabled={isLoading}
          className={selectClasses}
        >
          {Object.entries(PERIOD_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>

      {/* Dates custom — jamais désactivées : un input date qui se désactive en
          cours de frappe perd le focus et rend la saisie clavier impossible. */}
      {filters.period === DashboardPeriod.CUSTOM && (
        <>
          <div className="tw-flex tw-items-center tw-gap-2">
            <span className={labelClasses}>Du</span>
            <input
              type="date"
              value={draft.start}
              min={MIN_SELECTABLE_DATE}
              max={draft.end || today}
              onChange={(e) => handleDateChange('start', e.target.value)}
              onBlur={flushDates}
              onKeyDown={handleDateKeyDown}
              className={selectClasses}
            />
          </div>
          <div className="tw-flex tw-items-center tw-gap-2">
            <span className={labelClasses}>Au</span>
            <input
              type="date"
              value={draft.end}
              min={draft.start || MIN_SELECTABLE_DATE}
              max={today}
              onChange={(e) => handleDateChange('end', e.target.value)}
              onBlur={flushDates}
              onKeyDown={handleDateKeyDown}
              className={selectClasses}
            />
          </div>
          {(!filters.startDate || !filters.endDate) && (
            <span className="tw-text-xs tw-text-gray-400 tw-italic">
              Renseignez les deux dates pour lancer la recherche
            </span>
          )}
        </>
      )}

      {/* Point de vente */}
      {stores.length > 1 && (
        <div className="tw-flex tw-items-center tw-gap-2">
          <span className={labelClasses}>Point de vente</span>
          <select
            value={filters.storeId || ''}
            onChange={(e) => handleStoreChange(e.target.value)}
            disabled={isLoading}
            className={selectClasses}
          >
            <option value="">Tous les points de vente</option>
            {stores.map((store) => (
              <option key={store.id} value={store.id}>{store.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Utilisateur */}
      {availableUsers.length > 1 && (
        <div className="tw-flex tw-items-center tw-gap-2">
          <span className={labelClasses}>Utilisateur</span>
          <select
            value={filters.filterUserId || ''}
            onChange={(e) => handleUserChange(e.target.value)}
            disabled={isLoading}
            className={selectClasses}
          >
            <option value="">Tous les utilisateurs</option>
            {availableUsers.map((user) => (
              <option key={user.id} value={user.id}>{user.name}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};
